import React, { useState } from 'react';
import { Rate } from '../types/Rate';
import { MoreHorizontalIcon, PlusIcon, GripVerticalIcon } from 'lucide-react';
export function Rates() {
  // Mock data based on the screenshot
  const [rates, setRates] = useState<Rate[]>([{
    id: '1',
    name: 'Test With Scuba Spa',
    description: 'Scuba spa test',
    roomType: 'DELUXE',
    promoCode: 'EXPERIENCEMALDIVES',
    baseNightlyRate: 100.0,
    cancellationPlan: 'NON_REFUNDABLE',
    status: 'inactive',
    isPromo: true
  }, {
    id: '2',
    name: 'Advance Booking with Free Seaplan Transfer',
    description: 'Plan your Maldivan escape early and enjoy the added luxury of a complimentary round-trip seaplane transfer from Malé to the resort. This exclusive offer rewards advance planners with significant savings on transportation costs.',
    roomType: 'DELUXE',
    promoCode: 'EXPERIENCEMALDIVES2025',
    baseNightlyRate: 100.0,
    cancellationPlan: 'FLEXIBLE',
    status: 'active',
    isPromo: true
  }]);
  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [roomTypeFilter, setRoomTypeFilter] = useState<string>('all');
  // Filter rates based on search term and filters
  const filteredRates = rates.filter(rate => {
    const matchesSearch = rate.name.toLowerCase().includes(searchTerm.toLowerCase()) || rate.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || statusFilter === 'active' && rate.status === 'active' || statusFilter === 'inactive' && rate.status === 'inactive';
    const matchesRoomType = roomTypeFilter === 'all' || rate.roomType === roomTypeFilter;
    return matchesSearch && matchesStatus && matchesRoomType;
  });
  // Format currency for display
  function formatCurrency(amount: number): string {
    return `$${amount.toFixed(2)}`;
  }
  // Format cancellation plan for display
  function formatCancellationPlan(plan: string): string {
    return plan.replace('_', ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  }
  return <div className="space-y-6" data-id="element-3570">
      <div className="flex justify-between items-center" data-id="element-3571">
        <h1 className="text-2xl font-semibold text-gray-800" data-id="element-3572">Rates</h1>
        <button className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md flex items-center" data-id="element-3573">
          <PlusIcon size={18} className="mr-1" data-id="element-3574" />
          Add Rates
        </button>
      </div>
      {/* Filters */}
      <div className="flex flex-wrap gap-3" data-id="element-3575">
        <input type="text" placeholder="Search rates..." className="border border-gray-300 rounded-md px-3 py-2 w-48" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-3576" />
        <select className="border border-gray-300 rounded-md px-3 py-2" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-id="element-3577">
          <option value="all" data-id="element-3578">Status</option>
          <option value="active" data-id="element-3579">Active</option>
          <option value="inactive" data-id="element-3580">Inactive</option>
        </select>
        <select className="border border-gray-300 rounded-md px-3 py-2" value={roomTypeFilter} onChange={e => setRoomTypeFilter(e.target.value)} data-id="element-3581">
          <option value="all" data-id="element-3582">Room Type</option>
          <option value="DELUXE" data-id="element-3583">Deluxe</option>
          <option value="SUPERIOR" data-id="element-3584">Superior</option>
          <option value="OVERWATER_VILLA" data-id="element-3585">Overwater Villa</option>
        </select>
      </div>
      {/* Rates List */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-id="element-3586">
        <table className="min-w-full divide-y divide-gray-200" data-id="element-3587">
          <thead className="bg-gray-50" data-id="element-3588">
            <tr data-id="element-3589">
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3590">
                Details
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3591">
                Promo Code
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3592">
                Base Nightly Rate
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3593">
                Cancellation Plan
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3594">
                Status
              </th>
              <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3595">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-3596">
            {filteredRates.map(rate => <tr key={rate.id} data-id="element-3597">
                <td className="px-6 py-4" data-id="element-3598">
                  <div className="flex items-start" data-id="element-3599">
                    <GripVerticalIcon size={16} className="text-gray-400 mt-1 mr-2" data-id="element-3600" />
                    <div data-id="element-3601">
                      <div className="text-sm font-medium text-gray-900" data-id="element-3602">
                        {rate.name}
                      </div>
                      <div className="text-xs text-gray-500 mt-1 max-w-lg" data-id="element-3603">
                        {rate.description}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-3604">
                  <div className="text-sm text-gray-900" data-id="element-3605">{rate.promoCode}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-3606">
                  <div className="text-sm text-gray-900" data-id="element-3607">
                    {formatCurrency(rate.baseNightlyRate)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-3608">
                  <div className="text-sm text-gray-900" data-id="element-3609">
                    {formatCancellationPlan(rate.cancellationPlan)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-3610">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${rate.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-3611">
                    {rate.status === 'active' ? 'Active' : 'Inactive'}
                  </span>
                  {rate.isPromo && <span className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800" data-id="element-3612">
                      PROMO
                    </span>}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center" data-id="element-3613">
                  <button className="text-gray-400 hover:text-gray-500" data-id="element-3614">
                    <MoreHorizontalIcon size={18} data-id="element-3615" />
                  </button>
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}